import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

export const SESSION_START_ADJUSTMENT_CONTRACT_VERSION = "1.0.0" as const;
export const SESSION_START_ADJUSTMENT_PREVIEW_TTL_MS = 2 * 60 * 1_000;

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const CanonicalTimestampSchema = Type.String({ minLength: 20, maxLength: 64 });
const BusinessDateSchema = Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" });
const MoneyFenSchema = Type.Integer({
  minimum: Number.MIN_SAFE_INTEGER,
  maximum: Number.MAX_SAFE_INTEGER,
});

export const SessionStartAdjustmentPreviewSchema = Type.Object(
  {
    contractVersion: Type.Literal(SESSION_START_ADJUSTMENT_CONTRACT_VERSION),
    adjustmentKind: Type.Literal("UNPAID_TIME_ONLY_ACTIVE_SESSION"),
    tenantId: StableIdSchema,
    storeId: StableIdSchema,
    sessionId: StableIdSchema,
    orderId: StableIdSchema,
    orderNumber: Type.String({ minLength: 1, maxLength: 64 }),
    tableId: StableIdSchema,
    tableNumber: Type.String({ minLength: 1, maxLength: 64 }),
    expectedAggregateVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    billingState: Type.Union([Type.Literal("RUNNING"), Type.Literal("PAUSED")]),
    cashShiftId: StableIdSchema,
    cashShiftBusinessDate: BusinessDateSchema,
    cashShiftStartedAt: CanonicalTimestampSchema,
    generatedAt: CanonicalTimestampSchema,
    expiresAt: CanonicalTimestampSchema,
    previousStartedAt: CanonicalTimestampSchema,
    proposedStartedAt: CanonicalTimestampSchema,
    previousBusinessDate: BusinessDateSchema,
    proposedBusinessDate: BusinessDateSchema,
    previousTableFeeFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    proposedTableFeeFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    adjustmentFen: MoneyFenSchema,
    prepaidFen: Type.Literal(0),
    prepaidAdjustmentFen: Type.Literal(0),
    prepaymentOutcome: Type.Literal("NO_PREPAYMENT"),
    notificationAudience: Type.Tuple([Type.Literal("OWNER"), Type.Literal("MANAGER")]),
    notificationChannel: Type.Literal("BUSINESS_OPERATION_LOG"),
    notificationOutcome: Type.Literal("RECORDED_IN_SAME_TRANSACTION"),
    previewToken: Type.String({ minLength: 43, maxLength: 43, pattern: "^[A-Za-z0-9_-]{43}$" }),
  },
  { additionalProperties: false },
);
export type SessionStartAdjustmentPreview = Static<typeof SessionStartAdjustmentPreviewSchema>;

export const SessionStartAdjustmentCommandSchema = Type.Object(
  {
    preview: SessionStartAdjustmentPreviewSchema,
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type SessionStartAdjustmentCommand = Static<typeof SessionStartAdjustmentCommandSchema>;

export const SessionStartAdjustmentEventEvidenceSchema = Type.Object(
  {
    contractVersion: Type.Literal(SESSION_START_ADJUSTMENT_CONTRACT_VERSION),
    adjustmentKind: Type.Literal("UNPAID_TIME_ONLY_ACTIVE_SESSION"),
    actorRole: Type.Union([Type.Literal("MANAGER"), Type.Literal("CASHIER")]),
    cashShiftId: StableIdSchema,
    cashShiftBusinessDate: BusinessDateSchema,
    cashShiftStartedAt: CanonicalTimestampSchema,
    previewGeneratedAt: CanonicalTimestampSchema,
    previousStartedAt: CanonicalTimestampSchema,
    currentStartedAt: CanonicalTimestampSchema,
    previousBusinessDate: BusinessDateSchema,
    currentBusinessDate: BusinessDateSchema,
    previousTableFeeFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    currentTableFeeFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    adjustmentFen: MoneyFenSchema,
    prepaidFen: Type.Literal(0),
    prepaidAdjustmentFen: Type.Literal(0),
    notificationAudience: Type.Tuple([Type.Literal("OWNER"), Type.Literal("MANAGER")]),
    notificationChannel: Type.Literal("BUSINESS_OPERATION_LOG"),
    notificationOutcome: Type.Literal("RECORDED_IN_SAME_TRANSACTION"),
  },
  { additionalProperties: false },
);
export type SessionStartAdjustmentEventEvidence = Static<
  typeof SessionStartAdjustmentEventEvidenceSchema
>;

function canonicalTimestamp(value: string): boolean {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) && new Date(parsed).toISOString() === value;
}

function canonicalBusinessDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value);
  if (!match) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return (
    parsed.getUTCFullYear() === Number(match[1]) &&
    parsed.getUTCMonth() + 1 === Number(match[2]) &&
    parsed.getUTCDate() === Number(match[3])
  );
}

export function isSessionStartAdjustmentPreview(
  value: unknown,
): value is SessionStartAdjustmentPreview {
  if (!Value.Check(SessionStartAdjustmentPreviewSchema, value)) return false;
  const generatedAt = Date.parse(value.generatedAt);
  const expiresAt = Date.parse(value.expiresAt);
  return (
    canonicalTimestamp(value.generatedAt) &&
    canonicalTimestamp(value.expiresAt) &&
    canonicalTimestamp(value.previousStartedAt) &&
    canonicalTimestamp(value.proposedStartedAt) &&
    canonicalTimestamp(value.cashShiftStartedAt) &&
    canonicalBusinessDate(value.previousBusinessDate) &&
    canonicalBusinessDate(value.proposedBusinessDate) &&
    canonicalBusinessDate(value.cashShiftBusinessDate) &&
    value.previousStartedAt !== value.proposedStartedAt &&
    value.previousBusinessDate === value.proposedBusinessDate &&
    value.previousBusinessDate === value.cashShiftBusinessDate &&
    Date.parse(value.previousStartedAt) >= Date.parse(value.cashShiftStartedAt) &&
    Date.parse(value.proposedStartedAt) >= Date.parse(value.cashShiftStartedAt) &&
    generatedAt >= Date.parse(value.cashShiftStartedAt) &&
    expiresAt > generatedAt &&
    expiresAt - generatedAt === SESSION_START_ADJUSTMENT_PREVIEW_TTL_MS &&
    Number.isSafeInteger(value.previousTableFeeFen) &&
    Number.isSafeInteger(value.proposedTableFeeFen) &&
    Number.isSafeInteger(value.adjustmentFen) &&
    value.adjustmentFen === value.proposedTableFeeFen - value.previousTableFeeFen
  );
}

export function isSessionStartAdjustmentCommand(
  value: unknown,
): value is SessionStartAdjustmentCommand {
  return (
    Value.Check(SessionStartAdjustmentCommandSchema, value) &&
    value.reason === value.reason.trim() &&
    [...value.reason].length >= 2 &&
    [...value.reason].length <= 200 &&
    isSessionStartAdjustmentPreview(value.preview)
  );
}

export function isSessionStartAdjustmentEventEvidence(
  value: unknown,
): value is SessionStartAdjustmentEventEvidence {
  return (
    Value.Check(SessionStartAdjustmentEventEvidenceSchema, value) &&
    canonicalTimestamp(value.previewGeneratedAt) &&
    canonicalTimestamp(value.previousStartedAt) &&
    canonicalTimestamp(value.currentStartedAt) &&
    canonicalTimestamp(value.cashShiftStartedAt) &&
    canonicalBusinessDate(value.previousBusinessDate) &&
    canonicalBusinessDate(value.currentBusinessDate) &&
    canonicalBusinessDate(value.cashShiftBusinessDate) &&
    value.previousStartedAt !== value.currentStartedAt &&
    value.previousBusinessDate === value.currentBusinessDate &&
    value.previousBusinessDate === value.cashShiftBusinessDate &&
    Date.parse(value.previousStartedAt) >= Date.parse(value.cashShiftStartedAt) &&
    Date.parse(value.currentStartedAt) >= Date.parse(value.cashShiftStartedAt) &&
    Date.parse(value.previewGeneratedAt) >= Date.parse(value.cashShiftStartedAt) &&
    Number.isSafeInteger(value.previousTableFeeFen) &&
    Number.isSafeInteger(value.currentTableFeeFen) &&
    Number.isSafeInteger(value.adjustmentFen) &&
    value.adjustmentFen === value.currentTableFeeFen - value.previousTableFeeFen
  );
}
